import React, { useState, useEffect } from "react";
import {
  Container,
  Card,
  Table,
  Button,
  Badge,
  Modal,
  Alert,
  Spinner,
  Form,
  Row,
  Col,
  InputGroup,
} from "react-bootstrap";
import {
  FaPlus,
  FaEdit,
  FaTrash,
  FaEye,
  FaSearch,
  FaFilter,
  FaCalendarAlt,
  FaUser,
  FaTag,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Header from "../../components/common/Header";
import BlogService from "../../services/blogService";

const BlogManagement = () => {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedPost, setSelectedPost] = useState(null);

  useEffect(() => {
    loadPosts();
  }, [statusFilter, categoryFilter]);

  const loadPosts = async () => {
    try {
      setLoading(true);
      const status = statusFilter === "all" ? null : statusFilter;
      const category = categoryFilter === "all" ? null : categoryFilter;
      const response = await BlogService.adminGetAllBlogs(status, category);
      setPosts(response.data || []);
    } catch (error) {
      toast.error("Failed to load blog posts");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedPost) return;
    try {
      await BlogService.adminDeleteBlog(selectedPost.id);
      toast.success("Blog post deleted successfully");
      setShowDeleteModal(false);
      setSelectedPost(null);
      loadPosts();
    } catch (error) {
      toast.error("Failed to delete blog post");
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "published":
        return <Badge bg="success">Published</Badge>;
      case "draft":
        return <Badge bg="warning">Draft</Badge>;
      case "archived":
        return <Badge bg="secondary">Archived</Badge>;
      default:
        return <Badge bg="secondary">{status}</Badge>;
    }
  };

  const getCategoryBadge = (category) => {
    const colors = {
      blog: "primary",
      news: "info",
      announcement: "warning",
      event: "success",
    };
    return <Badge bg={colors[category] || "secondary"}>{category}</Badge>;
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const filteredPosts = posts.filter(
    (post) =>
      post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (post.author_name &&
        post.author_name.toLowerCase().includes(searchTerm.toLowerCase())),
  );

  if (loading) {
    return (
      <div>
        <Header />
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3">Loading blog posts...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Header />
      <Container fluid className="p-4">
        <Card className="shadow-sm">
          <Card.Header className="bg-primary text-white d-flex justify-content-between align-items-center">
            <h5 className="mb-0">
              <FaTag className="me-2" /> Blog Management
            </h5>
            <Button
              variant="light"
              size="sm"
              onClick={() => navigate("/admin/blog/create")}
            >
              <FaPlus className="me-1" /> Create Post
            </Button>
          </Card.Header>
          <Card.Body>
            {/* Filters */}
            <Row className="mb-4">
              <Col md={5}>
                <InputGroup>
                  <InputGroup.Text>
                    <FaSearch />
                  </InputGroup.Text>
                  <Form.Control
                    placeholder="Search by title or author..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </InputGroup>
              </Col>
              <Col md={3}>
                <InputGroup>
                  <InputGroup.Text>
                    <FaFilter />
                  </InputGroup.Text>
                  <Form.Select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                  >
                    <option value="all">All Status</option>
                    <option value="published">Published</option>
                    <option value="draft">Draft</option>
                    <option value="archived">Archived</option>
                  </Form.Select>
                </InputGroup>
              </Col>
              <Col md={3}>
                <InputGroup>
                  <InputGroup.Text>
                    <FaTag />
                  </InputGroup.Text>
                  <Form.Select
                    value={categoryFilter}
                    onChange={(e) => setCategoryFilter(e.target.value)}
                  >
                    <option value="all">All Categories</option>
                    <option value="blog">Blog</option>
                    <option value="news">News</option>
                    <option value="announcement">Announcement</option>
                    <option value="event">Event</option>
                  </Form.Select>
                </InputGroup>
              </Col>
              <Col md={1}>
                <Button
                  variant="outline-secondary"
                  onClick={loadPosts}
                  className="w-100"
                >
                  Refresh
                </Button>
              </Col>
            </Row>

            {filteredPosts.length === 0 ? (
              <Alert variant="info" className="text-center">
                <FaTag size={40} className="mb-3" />
                <h5>No blog posts found</h5>
                <p>Click "Create Post" to add your first blog post.</p>
              </Alert>
            ) : (
              <Table striped hover responsive>
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Title</th>
                    <th>Category</th>
                    <th>Author</th>
                    <th>Date</th>
                    <th>Views</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPosts.map((post) => (
                    <tr key={post.id}>
                      <td>{post.id}</td>
                      <td>
                        <strong>{post.title}</strong>
                        {post.short_description && (
                          <>
                            <br />
                            <small className="text-muted">
                              {post.short_description.substring(0, 50)}...
                            </small>
                          </>
                        )}
                      </td>
                      <td>{getCategoryBadge(post.category)}</td>
                      <td>{post.author_name || "Admin"}</td>
                      <td>
                        {formatDate(post.published_date || post.created_at)}
                      </td>
                      <td>{post.views || 0}</td>
                      <td>{getStatusBadge(post.status)}</td>
                      <td>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          className="me-1"
                          onClick={() =>
                            navigate(`/admin/blog/edit/${post.id}`)
                          }
                        >
                          <FaEdit />
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => {
                            setSelectedPost(post);
                            setShowDeleteModal(true);
                          }}
                        >
                          <FaTrash />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </Card.Body>
        </Card>
      </Container>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title className="text-danger">
            <FaTrash className="me-2" /> Confirm Delete
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to delete the post:</p>
          <p className="fw-bold text-danger">{selectedPost?.title}</p>
          <p className="text-muted">This action cannot be undone.</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete}>
            Delete Permanently
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default BlogManagement;
